import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Navigate,
  useLocation,
} from "react-router-dom";

import {
  clearAuthSession,
  getStoredToken,
  getTokenExpirationTime,
  isTokenExpired,
} from "../utils/authSession";

import {
  getCurrentUser,
  getFirstAllowedPath,
  getPermissionFingerprint,
  getRoutePermission,
  hasPermissionForUser,
  setCurrentUser,
  useCurrentUser,
} from "../utils/permissions";

import {
  refreshCurrentUserSession,
} from "../services/auth";

/*
 * These variables prevent duplicate /auth/me requests when:
 * - React StrictMode mounts the component twice in development
 * - ProtectedRoute is mounted again during route navigation
 * - multiple protected routes use the same login token
 */
let successfullyRefreshedToken = null;
let activeRefreshPromise = null;
let activeRefreshToken = null;

const refreshSessionOnlyOnce = (
  token
) => {
  /*
   * The current token has already been refreshed and the user
   * is available in storage, so no API request is required.
   */
  if (
    successfullyRefreshedToken ===
      token &&
    getCurrentUser()
  ) {
    return Promise.resolve(
      getCurrentUser()
    );
  }

  /*
   * A request for the same token is already running.
   * Return the same promise instead of creating another request.
   */
  if (
    activeRefreshPromise &&
    activeRefreshToken === token
  ) {
    return activeRefreshPromise;
  }

  activeRefreshToken = token;

  activeRefreshPromise =
    refreshCurrentUserSession()
      .then((result) => {
        /*
         * Mark the token as refreshed only when it is still
         * the active login token.
         */
        if (
          getStoredToken() === token
        ) {
          successfullyRefreshedToken =
            token;
        }

        return result;
      })
      .finally(() => {
        if (
          activeRefreshToken === token
        ) {
          activeRefreshPromise = null;
          activeRefreshToken = null;
        }
      });

  return activeRefreshPromise;
};

const LoadingAccess = () => (
  <div className="grid min-h-screen place-items-center bg-slate-50 dark:bg-slate-950">
    <div className="text-center">
      <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600 dark:border-slate-800 dark:border-t-blue-400" />

      <p className="mt-4 text-sm font-medium text-slate-500 dark:text-slate-400">
        Loading your access permissions...
      </p>
    </div>
  </div>
);

export default function ProtectedRoute({
  children,
}) {
  const location = useLocation();
  const user = useCurrentUser();

  const token = getStoredToken();

  const refreshingRef = useRef(false);
  const mountedRef = useRef(false);

  /*
   * Stores the token for which this component has already
   * started the initial /auth/me request.
   */
  const loadedTokenRef = useRef(null);

  const [checkingUser, setCheckingUser] =
    useState(() =>
      Boolean(
        token &&
          !isTokenExpired(token) &&
          !getCurrentUser()
      )
    );

  const [sessionExpired, setSessionExpired] =
    useState(() =>
      !token || isTokenExpired(token)
    );

  const invalidateSession =
    useCallback(() => {
      successfullyRefreshedToken = null;

      clearAuthSession();
      setCurrentUser(null);

      if (mountedRef.current) {
        setSessionExpired(true);
        setCheckingUser(false);
      }
    }, []);

  const refreshAccess = useCallback(
    async ({
      showLoading = false,
    } = {}) => {
      if (refreshingRef.current) {
        return;
      }

      refreshingRef.current = true;

      if (
        showLoading &&
        mountedRef.current
      ) {
        setCheckingUser(true);
      }

      try {
        const currentToken =
          getStoredToken();

        if (
          !currentToken ||
          isTokenExpired(currentToken)
        ) {
          invalidateSession();
          return;
        }

        /*
         * Calls /auth/me only once for the current login token.
         */
        await refreshSessionOnlyOnce(
          currentToken
        );

        if (mountedRef.current) {
          setSessionExpired(false);
        }
      } catch (error) {
        const status =
          error?.response?.status ||
          error?.statusCode ||
          null;

        if (
          [401, 403, 404].includes(
            status
          )
        ) {
          invalidateSession();
        } else {
          console.error(
            "AUTH USER LOAD ERROR:",
            error
          );

          /*
           * Do not log out the user during a temporary network
           * error when cached user data is still available.
           */
          if (
            !getCurrentUser() &&
            mountedRef.current
          ) {
            setSessionExpired(true);
          }
        }
      } finally {
        refreshingRef.current = false;

        if (mountedRef.current) {
          setCheckingUser(false);
        }
      }
    },
    [invalidateSession]
  );

  /*
   * JWT expiration timer.
   */
  useEffect(() => {
    mountedRef.current = true;

    const currentToken =
      getStoredToken();

    const expiresAt =
      getTokenExpirationTime(
        currentToken
      );

    if (
      !currentToken ||
      !expiresAt ||
      expiresAt <= Date.now()
    ) {
      invalidateSession();

      return () => {
        mountedRef.current = false;
      };
    }

    setSessionExpired(false);

    const remainingTime =
      expiresAt - Date.now();

    const timeoutId =
      window.setTimeout(
        invalidateSession,
        remainingTime
      );

    return () => {
      mountedRef.current = false;

      window.clearTimeout(
        timeoutId
      );
    };
  }, [token, invalidateSession]);

  /*
   * Load /auth/me only once for the current login token.
   *
   * Removed:
   * - 30-second interval
   * - browser focus refresh
   * - visibility-change refresh
   * - storage refresh
   * - permission event refresh
   */
  useEffect(() => {
    if (
      !token ||
      isTokenExpired(token)
    ) {
      return;
    }

    /*
     * Prevent this component from starting the request again
     * for the same token.
     */
    if (
      loadedTokenRef.current === token
    ) {
      return;
    }

    loadedTokenRef.current = token;

    refreshAccess({
      showLoading: !getCurrentUser(),
    });
  }, [token, refreshAccess]);

  const requiredPermission =
    useMemo(
      () =>
        getRoutePermission(
          location.pathname
        ),
      [location.pathname]
    );

  const permissionFingerprint =
    useMemo(
      () =>
        getPermissionFingerprint(user),
      [user]
    );

  if (
    !token ||
    sessionExpired ||
    isTokenExpired(token)
  ) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  if (checkingUser && !user) {
    return <LoadingAccess />;
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  if (
    requiredPermission &&
    !hasPermissionForUser(
      user,
      requiredPermission.moduleKey,
      requiredPermission.action
    )
  ) {
    const redirectPath =
      getFirstAllowedPath(user);

    if (
      redirectPath ===
      location.pathname
    ) {
      return null;
    }

    return (
      <Navigate
        to={redirectPath}
        replace
        state={{
          from: location.pathname,
          reason:
            "permission-denied",
        }}
      />
    );
  }

  /*
   * Remount the private layout only when actual permissions change.
   */
  return (
    <Fragment
      key={permissionFingerprint}
    >
      {children}
    </Fragment>
  );
}